/**
 * AI Lead Intake API client (ALI Option B).
 * Save = IntakeDraft upsert; Accept = createLeadWithReference.
 */

import api from "../../../shared/api.jsx";

/**
 * Staff ingest: paste/upload text + attachment refs → IntakeDraft.
 * @param {object} payload
 */
export async function ingestIntakeDraft(payload) {
  const response = await api.post("/leads/intake/ingest", payload);
  return response.data ?? response;
}

/**
 * Batch ingest → multiple IntakeDrafts (never Leads).
 * @param {{ items: object[], sourceType?: string, runAutofill?: boolean, awaitProviderPricing?: boolean }} payload
 */
export async function ingestIntakeDraftBatch(payload) {
  const response = await api.post("/leads/intake/ingest-batch", payload);
  return response.data ?? response;
}

/**
 * @param {{ status?: string, limit?: number, skip?: number }} [params]
 */
export async function listIntakeDrafts(params = {}) {
  const response = await api.get("/leads/intake", { params });
  return response.data ?? response;
}

/**
 * @param {string} draftId
 */
export async function getIntakeDraft(draftId) {
  const response = await api.get(`/leads/intake/${draftId}`);
  return response.data ?? response;
}

/**
 * Save draft fields (never creates Lead).
 * @param {string} draftId
 * @param {object} payload
 */
export async function saveIntakeDraft(draftId, payload) {
  const response = await api.put(`/leads/intake/${draftId}`, payload);
  return response.data ?? response;
}

/**
 * Accept draft → createLeadWithReference.
 * @param {string} draftId
 * @param {{ forceAccept?: boolean, fields?: object, awaitProviderPricing?: boolean, customerId?: string|null }} [payload]
 */
export async function acceptIntakeDraft(draftId, payload = {}) {
  const response = await api.post(`/leads/intake/${draftId}/accept`, payload);
  return response.data ?? response;
}

/**
 * Match existing customer by phone OR email (read-only). Persists customerId on draft.
 * @param {string} draftId
 * @param {{ phone?: string, email?: string, persist?: boolean, clear?: boolean, resolution?: string, preloadFields?: boolean, selectCustomerId?: string, fields?: object }} [payload]
 */
export async function matchIntakeCustomer(draftId, payload = {}) {
  const response = await api.post(
    `/leads/intake/${draftId}/match-customer`,
    payload
  );
  return response.data ?? response;
}

/**
 * Soft-discard one draft (status → discarded).
 * @param {string} draftId
 */
export async function discardIntakeDraft(draftId) {
  const response = await api.post(`/leads/intake/${draftId}/discard`);
  return response.data ?? response;
}

/**
 * Soft-discard all open drafts.
 */
export async function discardOpenIntakeDrafts() {
  const response = await api.post("/leads/intake/discard-open");
  return response.data ?? response;
}

/**
 * Upload files (images, PDF, DOC/DOCX, TXT) onto an intake draft.
 * @param {string} draftId
 * @param {File[]} files
 */
export async function uploadIntakeAttachments(draftId, files) {
  const formData = new FormData();
  for (const file of files || []) {
    formData.append("files", file);
  }
  const response = await api.post(
    `/leads/intake/${draftId}/attachments/upload`,
    formData
  );
  return response.data ?? response;
}

/**
 * @param {string} draftId
 * @param {string} attachmentId
 * @param {{ status: string, error?: string }} payload
 */
export async function patchIntakeAttachment(draftId, attachmentId, payload) {
  const response = await api.patch(
    `/leads/intake/${draftId}/attachments/${attachmentId}`,
    payload
  );
  return response.data ?? response;
}

/**
 * Export selected leads as provider (No-IP) or internal pricing CSV.
 * @param {{ leadIds: string[], includePii?: boolean }} payload
 * @returns {Promise<{ success: boolean, csv: string, filename: string, rowCount: number }>}
 */
export async function exportProviderPricingCsv(payload) {
  const response = await api.post(
    "/leads/export/provider-pricing-csv",
    payload
  );
  return response.data ?? response;
}

/**
 * Trigger browser download for a CSV string.
 * @param {string} csv
 * @param {string} filename
 */
export function downloadCsvText(csv, filename) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "export.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
